import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, MessageCircle, Clock, Instagram, Facebook } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const { config } = useSiteConfig();
  
  const createWhatsAppLink = (phone: string, message: string) => {
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodedMessage}`;
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.location'),
      content: t('contact.address'),
      link: 'https://maps.app.goo.gl/qm32gLjFHWT4drAa9',
      linkText: t('contact.getDirections')
    },
    {
      icon: MessageCircle,
      title: t('contact.whatsappTony'),
      content: config.phoneNumberTony,
      link: createWhatsAppLink(config.phoneNumberTony, config.whatsappMessageTonyEn),
      linkText: t('contact.whatsappButton')
    },
    {
      icon: MessageCircle,
      title: t('contact.whatsappNila'),
      content: config.phoneNumberNila,
      link: createWhatsAppLink(config.phoneNumberNila, config.whatsappMessageNilaId),
      linkText: t('contact.whatsappButton')
    }
  ];

  const businessHours = [
    { day: t('contact.hours.monday'), hours: t('contact.hours.mondayTime') },
    { day: t('contact.hours.saturday'), hours: t('contact.hours.saturdayTime') },
    { day: t('contact.hours.sunday'), hours: t('contact.hours.sundayTime') }
  ];

  const socialMedia = [
    {
      name: 'Instagram',
      icon: Instagram,
      handle: '@tncakesbali',
      link: 'https://instagram.com/tncakesbali',
      color: 'from-pink-500 to-red-500'
    },
    {
      name: 'Facebook',
      icon: Facebook,
      handle: 'TN Cakes Bali',
      link: 'https://facebook.com/tncakesbali',
      color: 'from-blue-500 to-blue-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 to-premium-50">
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-r from-burgundy-100 via-cream-100 to-premium-100">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-6">
              {t('contact.title')}
            </h1>
            <div className="w-24 h-1 gold-gradient rounded-full mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {contactInfo.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="premium-card text-center hover:scale-105 transition-transform duration-300"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-burgundy-400 to-burgundy-600 rounded-full flex items-center justify-center">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-serif font-semibold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 mb-4 whitespace-pre-line leading-relaxed">
                  {item.content}
                </p>
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-burgundy-600 hover:text-burgundy-700 font-medium transition-colors"
                >
                  {item.linkText} →
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Map & Business Hours */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6">
                {t('contact.findUs')}
              </h2>
              <div className="h-[500px] rounded-2xl overflow-hidden premium-shadow">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3944.2087!2d115.1598!3d-8.6792!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zOMKwNDAnNDUuMSJTIDExNcKwMDknMzUuMyJF!5e0!3m2!1sen!2sid!4v1620000000000!5m2!1sen!2sid"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="TN Cakes Bali Location"
                />
              </div>
            </motion.div>

            {/* Business Hours & Social Media */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Business Hours */}
              <div>
                <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Clock className="w-8 h-8 text-burgundy-600" />
                  {t('contact.businessHours')}
                </h2>
                <div className="premium-card">
                  <div className="space-y-4">
                    {businessHours.map((item, index) => (
                      <div key={index} className="flex justify-between items-center py-2">
                        <span className="font-medium text-gray-700">{item.day}</span>
                        <span className="text-burgundy-600 font-semibold">{item.hours}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 p-4 bg-burgundy-50 rounded-xl">
                    <p className="text-sm text-gray-600 text-center">
                      <strong>{t('common.note')}:</strong> {t('contact.bookingNote')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div>
                <h3 className="text-2xl font-serif font-bold text-gray-900 mb-6">
                  {t('contact.followUs')}
                </h3>
                <div className="space-y-4">
                  {socialMedia.map((social, index) => (
                    <a
                      key={index}
                      href={social.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 bg-white rounded-xl premium-shadow hover:scale-105 transition-transform duration-300"
                    >
                      <div className={`w-12 h-12 bg-gradient-to-br ${social.color} rounded-full flex items-center justify-center`}>
                        <social.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{social.name}</span>
                        <span className="text-gray-600">{social.handle}</span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
              {t('contact.faq.title')}
            </h2>
            <div className="w-24 h-1 gold-gradient rounded-full mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              'advance',
              'pickup', 
              'dietary',
              'cancellation'
            ].map((key, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="premium-card"
              >
                <h3 className="text-lg font-serif font-semibold text-gray-900 mb-3">
                  {t(`contact.faq.questions.${key}.question`)}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {t(`contact.faq.questions.${key}.answer`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-burgundy-600 to-yellow-600">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
              {t('globalCTA.title')}
            </h2>
            <p className="text-xl text-burgundy-100 max-w-3xl mx-auto mb-8 leading-relaxed">
              {t('globalCTA.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/inquiry"
                className="bg-white text-burgundy-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {t('globalCTA.orderNow')}
              </a>
              <a
                href="/portfolio"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-burgundy-600 transition-all duration-300"
              >
                {t('globalCTA.viewGallery')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
